import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Modal,
  Fade,
  Backdrop,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  PhotoCamera,
  VideoLibrary,
  Store,
  Collections,
  Logout,
  Delete,
  WarningAmber,
} from "@mui/icons-material";
import NavMenu from "../components/Menu";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser, validateToken } from "../store/authSlice";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import api from "../utils/axiosConfig";

const normalizeArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (typeof data === "object") return Object.values(data);
  return [];
};

const Dashboard = () => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [openDeleteBox, setOpenDeleteBox] = useState(false);
  const [selectedPet, setSelectedPet] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, isAuthenticated, initialized } = useSelector((state) => state.auth);

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // ✅ Fetch pets
  const fetchPets = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get("/api/getpets");
      const pets = normalizeArray(res.data);
      setPets(pets);
    } catch (err) {
      console.error("🐶 Error fetching pets:", err);
      setPets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialized && isAuthenticated) fetchPets();
  }, [initialized, isAuthenticated]);

  // ✅ Auth management
  useEffect(() => {
    if (!initialized) dispatch(validateToken());
  }, [dispatch, initialized]);

  useEffect(() => {
    if (initialized && !isAuthenticated) navigate("/login", { replace: true });
  }, [initialized, isAuthenticated, navigate]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login", { replace: true });
  };

  // ✅ Normalize image URL
  const normalizeImageUrl = (img) => {
    if (!img) return "/defaultdog.jpg";
    if (img.startsWith("http")) return img;
    const base = api.defaults.baseURL || "";
    const clean = img.replace(/^public[\\/]/, "");
    return `${base}/${clean}`;
  };

  // ✅ Handle Delete Modal
  const openDeleteModal = () => setOpenDeleteBox(true);
  const closeDeleteModal = () => {
    setSelectedPet("");
    setOpenDeleteBox(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPet) return showSnackbar("Please select a pet to delete", "warning");
    try {
      await api.delete(`/api/deletepet?name=${encodeURIComponent(selectedPet)}`);
      showSnackbar(`Deleted ${selectedPet}`, "success");
      setSelectedPet("");
      setOpenDeleteBox(false);
      fetchPets();
    } catch (err) {
      console.error("❌ Error deleting pet:", err);
      showSnackbar("Failed to delete pet", "error");
    }
  };

  // ✅ Loading screen
  if (!initialized) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #0D1117, #1B263B)",
          color: "#fff",
        }}
      >
        <CircularProgress sx={{ color: "#4FC3F7", mb: 2 }} />
        <Typography variant="h6">Loading Knight’s Kennel...</Typography>
      </Box>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflowY: "auto",
        background: "linear-gradient(135deg, #0D1117 0%, #1C2833 40%, #2C3E50 100%)",
      }}
    >
      <NavMenu />

      <Container maxWidth="lg" sx={{ textAlign: "center", color: "#fff", pt: 10, pb: 10, position: "relative" }}>
        {/* Logout */}
        <Box sx={{ position: "absolute", top: 20, right: 30 }}>
          <Button
            onClick={handleLogout}
            startIcon={<Logout />}
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.4)",
              color: "#fff",
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            Logout
          </Button>
        </Box>

        {/* Header */}
        <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2, textShadow: "0px 3px 8px rgba(0,0,0,0.6)" }}>
          Welcome, {user?.name || user?.username || user?.email || "Guest"} 👋
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 6,
            color: "rgba(255,255,255,0.8)",
            maxWidth: "700px",
            margin: "0 auto",
          }}
        >
          Manage your kennel with ease — upload photos, videos, or list items for sale.
        </Typography>

        {/* Dashboard Grid */}
        <Grid container spacing={4} justifyContent="center">
          {[
            {
              title: "Upload Pet Images",
              description: "Add new pet photos to your gallery.",
              link: "/upload",
              icon: <PhotoCamera sx={{ fontSize: 40, color: "#4FC3F7" }} />,
            },
            {
              title: "Upload Videos",
              description: "Share adorable puppy videos.",
              link: "/uploadvideo",
              icon: <VideoLibrary sx={{ fontSize: 40, color: "#FFB300" }} />,
            },
            {
              title: "Upload Item for Sale",
              description: "List puppies and accessories for sale.",
              link: "/uploaditemforsale",
              icon: <Store sx={{ fontSize: 40, color: "#81C784" }} />,
            },
            {
              title: "View Collages",
              description: "See all your pet collages in one place.",
              link: "/collages",
              icon: <Collections sx={{ fontSize: 40, color: "#BA68C8" }} />,
            },
          ].map((section, i) => (
            <Grid key={i} item xs={12} sm={6} md={3}>
              <Paper
                elevation={10}
                sx={{
                  height: "100%",
                  p: 4,
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.07)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
                  color: "#fff",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.05)",
                    background: "rgba(255,255,255,0.15)",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.6)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>{section.icon}</Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                  {section.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "rgba(255,255,255,0.8)" }}
                >
                  {section.description}
                </Typography>
                <Link to={section.link} style={{ textDecoration: "none" }}>
                  <Button
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
                      borderRadius: "8px",
                      px: 3,
                      py: 1,
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: "bold",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
                      },
                    }}
                  >
                    Go
                  </Button>
                </Link>
              </Paper>
            </Grid>
          ))}

          {/* 🗑️ Delete Pet Grid Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={10}
              sx={{
                height: "100%",
                p: 4,
                borderRadius: "18px",
                background: "rgba(255,0,0,0.1)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
                color: "#fff",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-6px) scale(1.05)",
                  background: "rgba(255,0,0,0.2)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.6)",
                },
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Delete sx={{ fontSize: 40, color: "#FF5252" }} />
              </Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                Delete a Pet
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: "rgba(255,255,255,0.8)" }}
              >
                Remove a pet permanently from your kennel.
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={openDeleteModal}
                sx={{
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                Delete Pet
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* 🐾 Recent Uploads Section */}
        <Box sx={{ mt: 10 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              mb: 3,
              textShadow: "0px 3px 6px rgba(0,0,0,0.6)",
            }}
          >
            🐾 Recent Uploads
          </Typography>

          {loading ? (
            <CircularProgress sx={{ color: "#4FC3F7" }} />
          ) : pets.length === 0 ? (
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>
              No uploads yet — start by adding your first pet photo!
            </Typography>
          ) : (
            <Grid container spacing={3} justifyContent="center">
              {pets.slice(0, 6).map((pet, idx) => (
                <Grid key={idx} item xs={6} sm={4} md={3}>
                  <Paper
                    elevation={6}
                    sx={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      background: "rgba(255,255,255,0.08)",
                      backdropFilter: "blur(8px)",
                      transition: "transform 0.3s ease",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                  >
                    <LazyLoadImage
                      src={normalizeImageUrl(pet.image)}
                      alt={pet.name}
                      effect="blur"
                      width="100%"
                      height="180px"
                      style={{ objectFit: "cover" }}
                    />
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        {pet.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {pet.coat || "Adorable pup"}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 10, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", mt: 4 }}
          >
            © {new Date().getFullYear()} Knight’s Kennel — Crafted with ❤️ by
            Robert Maiale
          </Typography>
        </Box>
      </Container>

      {/* 🧱 Delete Confirmation Modal */}
      <Modal
        open={openDeleteBox}
        onClose={closeDeleteModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 400 }}
      >
        <Fade in={openDeleteBox}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "#1E1E1E",
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              minWidth: 360,
              color: "#fff",
              textAlign: "center",
            }}
          >
            <WarningAmber sx={{ fontSize: 50, color: "#FFB74D", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Delete a Pet
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "rgba(255,255,255,0.7)" }}>
              Select a pet to delete from your kennel:
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={selectedPet}
                onChange={(e) => setSelectedPet(e.target.value)}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  borderRadius: 2,
                }}
              >
                {pets.map((p) => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={closeDeleteModal}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.3)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
                sx={{ fontWeight: "bold" }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Dashboard;
